'use client'

import { useMutation, useQuery } from '@apollo/client'
import { ChaineLogo } from '@chain-zen/ui'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'

import { CodeVerificationInput } from '../../../../components/code-verification'
import { UserNameForm } from '../../../../components/username-form'
import { ANALYTICS_EVENTS, ANALYTICS_PAGES } from '../../../../constants/analytics'
import { useAnalytics } from '../../../../contexts/analytics-context'
import { INITIATE_AUTH_OR_SIGNUP, VERIFY_CODE } from '../../../../data/mutations/auth'
import { GET_CHAT_USER_TOKEN } from '../../../../data/queries/auth'
import { useAuth } from '../../../../hooks/use-auth'
import { usePageView } from '../../../../hooks/use-page-view'
import { useUserQuery } from '../../../../hooks/use-user-query'
import type { IAuthenticateResponseData } from '../../../../types/auth'
import { graphQLErrorParser } from '../../../../utils/error-parser'
import { tokenUtils } from '../../../../utils/token-utils'
import { CarrierLoginForm } from './carrier-login-form'

interface CarrierLoginFlowProps {
  onFlowCompleted?: () => void
  onStepCompleted?: (step: 'login' | 'verification' | 'username') => void
}

type FlowStep = 'login' | 'verification' | 'username'

export const CarrierLoginFlow = ({ onStepCompleted, onFlowCompleted }: CarrierLoginFlowProps) => {
  const [currentStep, setCurrentStep] = useState<FlowStep>('login')
  const [isLoading, setIsLoading] = useState(false)
  const { captureEvent } = useAnalytics()
  usePageView(ANALYTICS_PAGES.carrierLoginFlow)
  const { loading: loadingUserDetails, myDetails } = useUserQuery()
  const { setAuthUserDetails, authUserDetails } = useAuth()

  // Check authentication status and user details
  useEffect(() => {
    const isAuthenticated = tokenUtils.isAuthenticated()

    if (isAuthenticated && myDetails) {
      // If user is authenticated and has completed onboarding, complete flow
      if (myDetails.status === 'Active') {
        onFlowCompleted?.()
        return
      }

      // If authenticated but no username, show username form
      if (!myDetails.username) {
        setCurrentStep('username')
      }
    }
  }, [myDetails, onFlowCompleted])

  // Auth initiation success handler
  const handleAuthInitiated = useCallback(() => {
    setCurrentStep('verification')
    onStepCompleted?.('login')
  }, [onStepCompleted])

  // Verification mutations and queries
  const [initiateAuth, { loading: resendingCode }] = useMutation(INITIATE_AUTH_OR_SIGNUP, {
    onCompleted: () => {
      toast.success('Verification code sent successfully')
    },
    onError: (error) => {
      console.error('Error resending verification code:', error)
      toast.error(error.message || 'Failed to resend verification code. Please try again.')
    }
  })

  const { refetch: getChatUserToken } = useQuery<{
    chatUserToken: {
      token: string
    }
  }>(GET_CHAT_USER_TOKEN, {
    onError: (error) => {
      toast.error(error.message || 'Failed to get chat user token')
    },
    skip: true
  })

  const [verifyCode, { loading: verifyingCode }] = useMutation(VERIFY_CODE, {
    onCompleted: async (response) => {
      const verifyCodeData: IAuthenticateResponseData = response.verifyCode
      if (verifyCodeData) {
        tokenUtils.setToken('Access-Token', verifyCodeData.accessToken)
        tokenUtils.setToken('Refresh-Token', verifyCodeData.refreshToken)

        if (authUserDetails) {
          setAuthUserDetails({
            ...authUserDetails,
            status: verifyCodeData.status
          })
        }

        if (verifyCodeData.status === 'Active') {
          const data = await getChatUserToken()
          if (data) {
            tokenUtils.setToken('User-Chat-Token', data.data.chatUserToken.token)
          }
          toast.success('Verification successful!')
          onFlowCompleted?.()
        } else {
          setCurrentStep('username')
          onStepCompleted?.('verification')
        }
      }
      setIsLoading(false)
    },
    onError: (e) => {
      const error = graphQLErrorParser(e)[0]
      if (
        'extensions' in error &&
        error.extensions &&
        typeof error.extensions === 'object' &&
        'session' in error.extensions &&
        authUserDetails
      ) {
        setAuthUserDetails({
          ...authUserDetails,
          session: error.extensions.session as string
        })
        toast.error('Invalid verification code. Please try again.')
      } else {
        toast.error(error.message || 'Invalid verification code. Please try again.')
      }
      setIsLoading(false)
    }
  })

  // Verification handlers
  const handleVerificationSubmit = async (pinInputValue: string) => {
    setIsLoading(true)
    if (!authUserDetails?.session || !authUserDetails?.userID) {
      toast.error('Session expired. Please login again.')
      setCurrentStep('login')
      return { error: true }
    }

    try {
      captureEvent(ANALYTICS_EVENTS.auth.verificationCodeSubmit, { fromPage: 'carrier-verification' })
      await verifyCode({
        variables: {
          code: pinInputValue,
          session: authUserDetails.session,
          userID: authUserDetails.userID
        }
      })
      return { error: false }
    } catch {
      return { error: true }
    }
  }

  const handleResendCode = async () => {
    const cleanedEmailPhone = authUserDetails?.emailOrPhone
    if (!cleanedEmailPhone) {
      toast.error('No email or phone number found')
      return
    }

    try {
      const sanitizedEmailPhone = cleanedEmailPhone.includes('@') ? cleanedEmailPhone : cleanedEmailPhone.slice(2)

      await initiateAuth({
        variables: {
          countryCode: '+1',
          createdVia: 'ChaineWebApp',
          emailOrPhone: sanitizedEmailPhone
        }
      })
    } catch (error) {
      console.error('Error resending verification code:', error)
      toast.error('Failed to resend verification code. Please try again.')
    }
  }

  if (loadingUserDetails) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <span className="loading loading-spinner loading-lg" />
      </div>
    )
  }

  return (
    <div className="w-full max-w-md mx-auto">
      {currentStep === 'login' && (
        <div className="space-y-6 items-center">
          <div className="space-y-2 md:space-y-3 text-center items-center align-middle">
            <div className="flex justify-center">
              <ChaineLogo size="md" data-testid="chaine-logo" />
            </div>
            <h1 className="text-3xl md:text-3xl font-semibold tracking-tight" data-testid="login-heading">
              Your carrier business,{' '}
              <span className="text-transparent bg-gradient-to-r from-gradient-rose via-gradient-pink to-gradient-purple bg-clip-text will-change-transform">
                prioritized.
              </span>
            </h1>
            <p className="text-md text-gray-600 dark:text-gray-300 font-normal" data-testid="login-subheading">
              Enter your email address or phone number to get started.
            </p>
          </div>
          <CarrierLoginForm onAuthInitiated={handleAuthInitiated} onError={(error) => toast.error(error)} />
        </div>
      )}

      {currentStep === 'verification' && (
        <div className="space-y-6">
          <div className="text-center space-y-2">
            <h1 className="text-2xl font-bold">Enter verification code</h1>
            <p className="text-muted-foreground">We just sent a verification code to {authUserDetails?.emailOrPhone}</p>
          </div>
          <CodeVerificationInput
            emailOrPhone={authUserDetails?.emailOrPhone || ''}
            loading={resendingCode || verifyingCode || isLoading}
            onResendCode={handleResendCode}
            onSubmit={handleVerificationSubmit}
          />
        </div>
      )}

      {currentStep === 'username' && (
        <div className="space-y-6">
          <div className="text-center space-y-2">
            <h1 className="text-2xl font-bold">Set up your profile</h1>
            <p className="text-muted-foreground">Let's create your username to get started</p>
          </div>
          <UserNameForm isCarrierOnboarding={true} />
        </div>
      )}
    </div>
  )
}
